import { SimpleMenu } from "@repo/ui/Menu/simpleMenu";
import { SimpleMenuItem } from "@repo/ui/Menu/simpleMenuItem";
import { SimpleMenuPopover } from "@repo/ui/Menu/simpleMenuPopover";
import {
  IconExternalLink,
  IconPasswordFingerprint,
  IconSelector,
} from "@tabler/icons-react";
import type { FC } from "react";
import {
  Button as AriaButton,
  MenuTrigger,
  type Placement,
} from "react-aria-components";
import { twMerge } from "tailwind-merge";

interface UserProfileMenuItemBase {
  key: string;
  label: string;
}

/** Menu entry that navigates to a route. */
export interface UserProfileNavigationItem extends UserProfileMenuItemBase {
  href: string;
  /** Opens the link in a new tab and marks it with an external-link icon. */
  opensInNewWindow?: boolean;
}

/** Menu entry that runs a handler instead of navigating. */
export interface UserProfileActionItem extends UserProfileMenuItemBase {
  onAction: () => void;
}

export type UserProfileMenuItem =
  | UserProfileNavigationItem
  | UserProfileActionItem;

/** The service access session the profile item stands for. */
export interface UserProfileServiceAccess {
  companyName: string;
  workspaceAlias: string;
  /** The pill's text while ending soon, such as "Expiring in 12m". */
  expiringIn: string;
  /** Whether the session ends soon, which shows in the warning color. */
  isEndingSoon: boolean;
  /** Whether the session has ended, which turns the item into a sign-in button. */
  hasEnded: boolean;
  onEnd: () => void;
  onSignIn: () => void;
}

export interface UserProfileWidgetProps {
  name: string;
  email: string;
  menuItems: readonly UserProfileMenuItem[];
  /** Where the menu opens relative to the trigger. Defaults to the sidebar-friendly "top start". */
  menuPlacement?: Placement;
  /** Set in a service access session, which then shows in place of the email. */
  serviceAccess?: UserProfileServiceAccess;
}

export const UserProfileWidget: FC<UserProfileWidgetProps> = (props) => {
  const { name, email, serviceAccess } = props;
  if (serviceAccess?.hasEnded) {
    return (
      <AriaButton
        aria-label={`Sign in again to service access for ${serviceAccess.companyName} (${serviceAccess.workspaceAlias})`}
        onPress={serviceAccess.onSignIn}
        className={twMerge(
          userProfileButtonClassName,
          "bg-red-100 hover:bg-red-200",
        )}
      >
        <UserProfileButtonContents
          name={name}
          email={email}
          serviceAccess={serviceAccess}
        />
        <IconPasswordFingerprint
          aria-hidden
          className="size-4 shrink-0 text-sidebar-foreground"
        />
      </AriaButton>
    );
  }
  return <UserProfileMenu {...props} />;
};

const userProfileButtonClassName =
  "flex w-full items-center gap-2 rounded-xl px-2.5 py-2 text-left hover:bg-sidebar-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-primary";

const UserProfileMenu: FC<UserProfileWidgetProps> = ({
  name,
  email,
  menuItems,
  menuPlacement = "top start",
  serviceAccess,
}) => (
  <MenuTrigger>
    <AriaButton
      aria-label={
        serviceAccess
          ? `Open user menu, service access to ${serviceAccess.companyName} (${serviceAccess.workspaceAlias})`
          : "Open user menu"
      }
      className={twMerge(
        userProfileButtonClassName,
        serviceAccess?.isEndingSoon && "bg-amber-100 hover:bg-amber-200",
      )}
    >
      <UserProfileButtonContents
        name={name}
        email={email}
        serviceAccess={serviceAccess}
      />
      <IconSelector
        aria-hidden
        className="size-4 shrink-0 text-sidebar-foreground"
      />
    </AriaButton>
    <SimpleMenuPopover placement={menuPlacement}>
      <SimpleMenu aria-label="User menu">
        {menuItems.map((item) =>
          "href" in item ? (
            <SimpleMenuItem
              key={item.key}
              href={item.href}
              target={item.opensInNewWindow ? "_blank" : undefined}
              rel={item.opensInNewWindow ? "noreferrer" : undefined}
              textValue={item.opensInNewWindow ? item.label : undefined}
            >
              {item.opensInNewWindow ? (
                <>
                  <span className="min-w-0 flex-1 truncate" title={item.label}>
                    {item.label}
                  </span>
                  <IconExternalLink
                    aria-hidden
                    className="size-4 shrink-0 text-muted-foreground"
                  />
                </>
              ) : (
                item.label
              )}
            </SimpleMenuItem>
          ) : (
            <SimpleMenuItem key={item.key} onAction={item.onAction}>
              {item.label}
            </SimpleMenuItem>
          ),
        )}
        {serviceAccess ? (
          <SimpleMenuItem onAction={serviceAccess.onEnd}>
            Sign out
          </SimpleMenuItem>
        ) : null}
      </SimpleMenu>
    </SimpleMenuPopover>
  </MenuTrigger>
);

interface UserProfileButtonContentsProps {
  name: string;
  email: string;
  serviceAccess: UserProfileServiceAccess | undefined;
}

/** The pill, name and account lines of the profile item's button. */
const UserProfileButtonContents: FC<UserProfileButtonContentsProps> = (
  props,
) => {
  const { name, email, serviceAccess } = props;
  return (
    <span className="flex min-w-0 flex-1 flex-col leading-tight">
      {serviceAccess ? (
        <span
          className={twMerge(
            "mb-1 w-fit rounded-full bg-slate-900 px-2 py-0.5 text-xs font-medium text-white",
            serviceAccess.isEndingSoon && "bg-amber-600",
            serviceAccess.hasEnded && "bg-destructive",
          )}
        >
          {serviceAccess.hasEnded
            ? "Expired"
            : serviceAccess.isEndingSoon
              ? serviceAccess.expiringIn
              : "Service access"}
        </span>
      ) : null}
      <span className="truncate text-sm font-semibold text-sidebar-foreground">
        {name}
      </span>
      {serviceAccess ? (
        <>
          <span className="truncate text-xs text-foreground">
            {serviceAccess.companyName}
          </span>
          <span className="truncate text-xs text-muted-foreground">
            {serviceAccess.workspaceAlias}
          </span>
        </>
      ) : (
        <span className="truncate text-xs text-foreground">{email}</span>
      )}
    </span>
  );
};
